import { useState, type FC, memo } from 'react'
import { AreaChart, Button } from '@egw/components'
import cx from 'classnames'
import { useTranslation } from '@egw/i18n'
import { useHistoricalStockPrices } from '@egw/api'
import { type DataPoint, filterDataByPeriod, TimePeriod, transformStockData } from '../utils'

export interface TimeseriesChartProps {
  symbol: string
  className?: string
  timespan?: string
}

export const TimeseriesChart: FC<TimeseriesChartProps> = memo(({ symbol, className, timespan }) => {
  const { t } = useTranslation('features-chat')
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    timespan ? (timespan as TimePeriod) : TimePeriod.OneMonth,
  )

  const [selectedDataPoint, setSelectedDataPoint] = useState<DataPoint | null>(null)

  const { data, error } = useHistoricalStockPrices({
    ticker: symbol.toUpperCase() || '',
    startDate: new Date(new Date().getFullYear() - 5, new Date().getMonth(), new Date().getDate())
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  })

  if (!data || data.__typename === 'HistoricalStockPricesError' || !data.prices || error) {
    return null
  }

  const chartData = transformStockData(data.prices)
  const filteredData = chartData.length > 0 ? filterDataByPeriod(chartData, selectedPeriod) : []

  const displayData = filteredData.length > 0 ? filteredData : chartData.slice(-1)
  const hasData = displayData.length > 0

  const todayPrice = chartData[0]?.value ?? 0
  const periodStartPrice = hasData ? (displayData[displayData.length - 1]?.value ?? todayPrice) : 0

  const displayPrice = selectedDataPoint ? selectedDataPoint.value : todayPrice

  const totalChange = todayPrice - periodStartPrice
  const totalReturn = periodStartPrice > 0 ? (totalChange / periodStartPrice) * 100 : 0
  const isPositive = totalReturn >= 0

  const timePeriods: { value: TimePeriod; label: string; description: string }[] = [
    { value: TimePeriod.OneMonth, label: t('timeseries-chart.1M'), description: t('timeseries-chart.pastMonth') },
    { value: TimePeriod.ThreeMonths, label: t('timeseries-chart.3M'), description: t('timeseries-chart.past3Months') },
    { value: TimePeriod.SixMonths, label: t('timeseries-chart.6M'), description: t('timeseries-chart.past6Months') },
    { value: TimePeriod.OneYear, label: t('timeseries-chart.1Y'), description: t('timeseries-chart.pastYear') },
    { value: TimePeriod.FiveYears, label: t('timeseries-chart.5Y'), description: t('timeseries-chart.past5Years') },
  ]

  return (
    <div className={cx('bg-background-surface rounded-lg p-md', className)}>
      {/* Header */}
      <div className="flex flex-col gap-3xs my-2xs">
        <div className="flex items-center justify-between">
          <h2 className="typo-primary-sm text-text-meta">
            {data.name} ({data.ticker})
          </h2>
        </div>

        {/* Price and Change */}
        <div className="typo-primary-3xl-regular text-text-header">${displayPrice.toFixed(2)}</div>
        <div className="flex items-center gap-3xs">
          <span className={cx('typo-primary-sm-semibold', isPositive ? 'text-[#008518]' : 'text-[#DA3939]')}>
            {isPositive ? '⏶' : '⏷'} {Math.abs(totalReturn).toFixed(2)}%
          </span>
          <span className="typo-primary-sm-regular text-text-meta">
            {timePeriods.find((p) => p.value === selectedPeriod)?.description}
          </span>
        </div>
      </div>

      {/* Time Period Buttons */}
      <div className="flex gap-xs my-5">
        {timePeriods.map(({ value, label }) => (
          <Button
            key={value}
            onClick={() => {
              setSelectedPeriod(value)
            }}
            variant={selectedPeriod === value ? 'primary' : 'secondary'}
            size="small"
            className={cx(
              'px-sm py-2xs rounded-full',
              selectedPeriod === value
                ? 'bg-[#6C6451] hover:bg-[#6C6451] active:bg-[#6C6451] typo-primary-sm-bold text-white'
                : 'text-[#6C6451] border-[#ECE8DF] shadow-none hover:border-[#6C6451] hover:text-[#6C6451] hover:bg-gray-50 typo-primary-sm-semibold !hover:typo-primary-sm-bold',
            )}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-[300px] w-full">
        {filteredData.length > 0 ? (
          <AreaChart
            data={[...filteredData].reverse()}
            className="w-full h-full"
            onDataPointClick={setSelectedDataPoint}
            isPositive={isPositive}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-text-meta">{t('common.no_data_for_period')}</div>
        )}
      </div>
    </div>
  )
})

TimeseriesChart.displayName = 'TimeseriesChart'
